import tw from 'twin.macro';

import { GetSessionsStatsQuery, SessionStatus } from '@/libs/graphql';
import { STATUS } from '@/routes/dashboard/types';

const STATUS_CONFIGS = [
  {
    label: STATUS.NOT_STARTED,
    backendStatus: SessionStatus.NotStarted,
    dotStyle: tw`bg-gray-500`,
    activeBar: tw`bg-gray-100 text-gray-700 ring-1 ring-gray-300`,
    activeBadge: tw`bg-gray-200 text-gray-700`,
    hoverStyle: tw`hover:bg-gray-50`,
  },
  {
    label: STATUS.IN_PROGRESS,
    backendStatus: SessionStatus.InProgress,
    dotStyle: tw`bg-amber-500`,
    activeBar: tw`bg-amber-100 text-amber-700 ring-1 ring-amber-300`,
    activeBadge: tw`bg-amber-100 text-amber-700`,
    hoverStyle: tw`hover:bg-amber-50`,
  },
  {
    label: STATUS.COMPLETED,
    backendStatus: SessionStatus.Completed,
    dotStyle: tw`bg-emerald-500`,
    activeBar: tw`bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300`,
    activeBadge: tw`bg-emerald-100 text-emerald-700`,
    hoverStyle: tw`hover:bg-emerald-50`,
  },
  {
    label: STATUS.OVERDUE,
    backendStatus: SessionStatus.Expired,
    dotStyle: tw`bg-red-500`,
    activeBar: tw`bg-red-100 text-red-700 ring-1 ring-red-300`,
    activeBadge: tw`bg-red-100 text-red-700`,
    hoverStyle: tw`hover:bg-red-50`,
  },
  {
    label: STATUS.INCOMPLETE,
    backendStatus: SessionStatus.ExpiredInSession,
    dotStyle: tw`bg-orange-500`,
    activeBar: tw`bg-orange-100 text-orange-700 ring-1 ring-orange-300`,
    activeBadge: tw`bg-orange-100 text-orange-700`,
    hoverStyle: tw`hover:bg-orange-50`,
  },
];

const StatsCards = ({
  data,
  loading,
  activeStatus,
  onStatusClick,
}: {
  data: GetSessionsStatsQuery['getSessionsStats'] | undefined;
  loading: boolean;
  activeStatus: SessionStatus | null;
  onStatusClick: (status: SessionStatus | null) => void;
}) => {
  if (loading) {
    return (
      <div tw="flex flex-wrap items-center gap-3 bg-white p-1 rounded-xl border border-gray-200 animate-pulse">
        <div tw="flex items-center gap-2 px-4 py-2 border-r border-gray-200">
          <div tw="h-3 w-12 bg-gray-200 rounded" />
          <div tw="h-6 w-8 bg-gray-200 rounded" />
        </div>
        <div tw="flex gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} tw="h-8 w-28 bg-gray-100 rounded-full" />
          ))}
        </div>
      </div>
    );
  }

  const getCount = (backendStatus: SessionStatus): number =>
    data?.statusCounts.find((sc) => sc.status === backendStatus)?.count ?? 0;

  return (
    <div tw="flex flex-wrap items-center gap-3 bg-white p-1 rounded-xl border border-gray-200">
      <div tw="flex items-center gap-2 px-4 py-2 border-r border-gray-200 shrink-0">
        <span tw="text-xs font-semibold text-gray-500 uppercase tracking-wide">ทั้งหมด</span>
        <span tw="text-lg font-bold text-gray-900">{data?.total ?? 0}</span>
      </div>
      <div tw="flex flex-wrap gap-1">
        {STATUS_CONFIGS.map(({ label, backendStatus, dotStyle, activeBar, activeBadge, hoverStyle }) => {
          const isActive = activeStatus === backendStatus;
          const count = getCount(backendStatus);
          return (
            <button
              key={label}
              type="button"
              css={[
                tw`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors`,
                isActive ? activeBar : hoverStyle,
              ]}
              onClick={() => onStatusClick(isActive ? null : backendStatus)}
            >
              <span css={[tw`w-2 h-2 rounded-full shrink-0`, dotStyle]} />
              <span tw="text-sm font-medium text-gray-600">{label}</span>
              <span
                css={[
                  tw`px-1.5 py-0.5 rounded text-xs font-bold`,
                  isActive ? activeBadge : tw`bg-gray-100 text-gray-600`,
                ]}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default StatsCards;
